import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Heart, Share2, Download, X, ChevronLeft, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "../utils/LanguageContext";
import { fetchAllGalleries, getImageSrc } from "../apiService";
import Footer from "../components/footer";

const GalleryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  
  const [gallery, setGallery] = useState(location.state?.gallery || null);
  const [loading, setLoading] = useState(!gallery);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [likedPhotos, setLikedPhotos] = useState(new Set());

  useEffect(() => {
    if (!gallery) {
      loadGallery();
    }
  }, [id, gallery]);

  const loadGallery = async () => {
    setLoading(true);
    try {
      const galleries = await fetchAllGalleries();
      const foundGallery = galleries.find(g => g._id === id || g.slug === id);
      if (foundGallery) {
        setGallery(foundGallery);
      } else {
        // Gallery not found, redirect back
        navigate('/photo-album');
      }
    } catch (error) {
      console.error('Error loading gallery:', error);
      navigate('/photo-album');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoClick = (photo, index) => {
    setSelectedPhoto(photo);
    setCurrentPhotoIndex(index);
  };

  const handlePrevPhoto = () => {
    if (gallery?.photos && currentPhotoIndex > 0) {
      const newIndex = currentPhotoIndex - 1;
      setCurrentPhotoIndex(newIndex);
      setSelectedPhoto(gallery.photos[newIndex]);
    }
  };

  const handleNextPhoto = () => {
    if (gallery?.photos && currentPhotoIndex < gallery.photos.length - 1) {
      const newIndex = currentPhotoIndex + 1;
      setCurrentPhotoIndex(newIndex);
      setSelectedPhoto(gallery.photos[newIndex]);
    }
  };

  const handleLikePhoto = (photoIndex) => {
    setLikedPhotos(prev => {
      const newSet = new Set(prev);
      newSet.has(photoIndex) ? newSet.delete(photoIndex) : newSet.add(photoIndex);
      return newSet;
    });
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: gallery.title,
          text: gallery.description || (language === 'MR' ? "हे फोटो अल्बम पहा" : "Check out this photo album"),
          url: window.location.href,
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        alert(language === 'MR' ? "लिंक कॉपी केला गेला!" : "Link copied!");
      }
    } catch (error) {
      console.log("Sharing failed:", error);
    }
  };

  const handleDownload = (photo) => {
    if (photo.imageUrl) {
      const link = document.createElement('a');
      link.href = photo.imageUrl;
      link.download = photo.filename || 'photo.jpg';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'MR' ? 'mr-IN' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-24">
        <div className="text-center">
          <div className="w-20 h-20 border-4 border-gray-200 border-t-red-600 rounded-full animate-spin mx-auto mb-6"></div>
          <p className="text-red-700 font-semibold text-lg font-marathi">
            {language === 'MR' ? 'गॅलरी लोड होत आहे...' : 'Loading gallery...'}
          </p>
        </div>
      </div>
    );
  }

  if (!gallery) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-24">
        <div className="text-center">
          <p className="text-gray-500 text-lg font-marathi mb-4">
            {language === 'MR' ? 'गॅलरी सापडली नाही' : 'Gallery not found'}
          </p>
          <button
            onClick={() => navigate('/photo-album')}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-marathi"
          >
            {language === 'MR' ? 'परत जा' : 'Go Back'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-20">
        {/* Header */}
        <div className="mb-8">
          <motion.button
            onClick={() => navigate('/photo-album')}
            className="flex items-center gap-2 text-red-600 hover:text-red-800 mb-6 font-marathi"
            whileHover={{ x: -5 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <ArrowLeft className="w-5 h-5" />
            {language === 'MR' ? 'परत जा' : 'Back to Albums'}
          </motion.button>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-red-800 font-marathi mb-2">
                {gallery.title}
              </h1>
              {gallery.description && (
                <p className="text-gray-600 font-marathi text-lg">
                  {gallery.description}
                </p>
              )}
              <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                {gallery.createdAt && (
                  <span>{formatDate(gallery.createdAt)}</span>
                )}
                {gallery.author && (
                  <span>{gallery.author}</span>
                )}
                <span>
                  {gallery.photos?.length || 0} {language === 'MR' ? 'फोटो' : 'photos'}
                </span>
              </div>
            </div>

            <div className="flex gap-2">
              <motion.button
                onClick={handleShare}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors font-marathi"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Share2 className="w-4 h-4" />
                {language === 'MR' ? 'शेअर करा' : 'Share'}
              </motion.button>
            </div>
          </div>
        </div>

        {/* Photo Grid */}
        {gallery.photos && gallery.photos.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {gallery.photos.map((photo, index) => (
              <motion.div
                key={index}
                className="group relative bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer"
                onClick={() => handlePhotoClick(photo, index)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                whileHover={{ y: -5 }}
              >
                <div className="aspect-square overflow-hidden">
                  <motion.img
                    src={getImageSrc(photo.imageUrl)}
                    alt={photo.caption || `Photo ${index + 1}`}
                    className="w-full h-full object-cover"
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.3 }}
                  />
                </div>

                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <div className="flex gap-2">
                    <motion.button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLikePhoto(index);
                      }}
                      className={`p-2 rounded-full backdrop-blur-sm transition-all duration-200 ${
                        likedPhotos.has(index)
                          ? "bg-red-500 text-white"
                          : "bg-white/20 text-white hover:bg-white/30"
                      }`}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <Heart
                        className="w-4 h-4"
                        fill={likedPhotos.has(index) ? "currentColor" : "none"}
                      />
                    </motion.button>
                    <motion.button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(photo);
                      }}
                      className="p-2 rounded-full bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm transition-all duration-200"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <Download className="w-4 h-4" />
                    </motion.button>
                  </div>
                </div>

                {/* Caption */}
                {photo.caption && (
                  <div className="p-3">
                    <p className="text-sm text-gray-600 font-marathi line-clamp-2">
                      {photo.caption}
                    </p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="text-gray-500 text-lg font-marathi">
              {language === 'MR' ? 'या अल्बममध्ये कोणतेही फोटो नाहीत' : 'No photos in this album'}
            </p>
          </div>
        )}
      </main>

      {/* Photo Modal */}
      <AnimatePresence>
        {selectedPhoto && (
          <motion.div
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedPhoto(null)}
          >
            <motion.div
              className="relative max-w-4xl max-h-full"
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={() => setSelectedPhoto(null)}
                className="absolute -top-12 right-0 text-white hover:text-gray-300 z-10"
              >
                <X className="w-8 h-8" />
              </button>

              {/* Navigation Buttons */}
              {gallery.photos && gallery.photos.length > 1 && (
                <>
                  {currentPhotoIndex > 0 && (
                    <button
                      onClick={handlePrevPhoto}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 bg-black/20 rounded-full p-2 backdrop-blur-sm"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                  )}
                  {currentPhotoIndex < gallery.photos.length - 1 && (
                    <button
                      onClick={handleNextPhoto}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 bg-black/20 rounded-full p-2 backdrop-blur-sm"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                  )}
                </>
              )}

              {/* Photo */}
              <img
                src={getImageSrc(selectedPhoto.imageUrl)}
                alt={selectedPhoto.caption || 'Photo'}
                className="max-w-full max-h-full object-contain"
              />

              {/* Photo Info */}
              {selectedPhoto.caption && (
                <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white p-4 backdrop-blur-sm">
                  <p className="font-marathi">{selectedPhoto.caption}</p>
                </div>
              )}

              {/* Photo Counter */}
              {gallery.photos && gallery.photos.length > 1 && (
                <div className="absolute top-4 left-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm backdrop-blur-sm">
                  {currentPhotoIndex + 1} / {gallery.photos.length}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style jsx global>{`
        @font-face {
          font-family: "Marathi";
          src: url("/fonts/NotoSansDevanagari-Regular.ttf") format("truetype");
          font-display: swap;
        }
        .font-marathi {
          font-family: "Marathi", "Noto Sans Devanagari", sans-serif;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
      <Footer />
    </div>
  );
};

export default GalleryDetail;